import getUserTickets from "@/api/requests/getUserTickets";
import Order, { OrderStatus } from "@/types/Order";
import { defineStore } from "pinia";
import { ref, watch } from "vue";
import { PaginationControls } from "@/types/PaginationControls";
import getOrderDetails from "@/api/requests/getOrderDetails";

export const useOrdersStore = defineStore("orders", () => {
  const list = ref<Order[]>([]);

  const selected = ref<Order | null>(null);

  const loading = ref(false);

  const pagination = ref<PaginationControls>({
    page: 1,
    pageSize: 6,
    totalPages: 0,
    totalItems: 0,
  });

  async function fetchOrders(search = "", status: OrderStatus = OrderStatus.CONFIRMED): Promise<void> {
    try {
      loading.value = true;

      let _status: string = status;

      // At the request of M, the page should load with the paid *and* courtesy tickets.
      // Therefore, the status is now a csv of possible statuses. Sorry.
      if (status === OrderStatus.CONFIRMED) {
        _status += `,${OrderStatus.COURTESY}`;
      }

      const response = await getUserTickets(pagination.value.page, pagination.value.pageSize, search, _status);

      pagination.value.totalPages = response.last_page;
      pagination.value.totalItems = response.total;
      list.value = response.data;
    } catch (error) {
      console.error(error);
      throw new Error("Não foi possível buscar seus ingressos. Tente mais tarde.");
    } finally {
      loading.value = false;
    }
  }

  async function selectOrderById(id: string): Promise<void> {
    try {
      selected.value = await getOrderDetails(id);
    } catch (error) {
      console.error(error);
      throw new Error("Não foi possível carregar os detalhes do ingresso. Tente mais tarde.");
    }
  }

  function select(order: Order): void {
    selected.value = order;
  }

  watch(
    () => pagination.value.page,
    () => {
      fetchOrders();
    },
  );

  return {
    list,
    loading,
    selected,
    pagination,
    select,
    selectOrderById,
    fetchOrders,
  };
}, { persist: { pick: [ "selected" ] } });
